/**
 * The build's environment variables, each as a plain string.
 *
 * Read through this one file rather than import.meta.env at each use, so
 * every variable has a default (empty, or production's API) and a type, and
 * nothing that reads one has to ask whether it is undefined.
 *
 * Each variable is named here one by one. Vite writes into the bundle only
 * the variables the code names; spreading the whole of import.meta.env
 * wrote every VITE_ variable the build had, used or not, into the public
 * JavaScript (an unused Google API key among them).
 *
 * Used by: config/firebase (the three Firebase values), shared/api/shares
 * (VITE_SHARE_BASE_URL), and shared/components/auth/GoogleOneTap and
 * shared/services/GooglePhotosService (VITE_GOOGLE_CLIENT_ID). The Google
 * Maps key went with the browser's geocoder (W24 Job E): the server places
 * the garden from its ZIP.
 *
 * Env vars: the six named in getEnvVars below, all public once built.
 *
 * Surprising: this is not the only door. shared/api/apiClient,
 * shared/context/AuthContext and the public share pages read VITE_API_URL,
 * VITE_USE_MOCK_AUTH and VITE_DEV_FIREBASE_UID straight from import.meta.env,
 * and shared/lib/sentry reads its own four; so this file's VITE_API_URL (with
 * its production fallback) is read by nobody, and apiClient has no fallback
 * at all. The full list of what reaches the browser is in src/vite-env.d.ts.
 *
 * @module env-vars
 */

import { PRODUCTION_API_URL } from '@/config/tunables/shared';

/** What each variable is when the build was not given it. */
const defaultValues = {
  VITE_API_URL: PRODUCTION_API_URL,
  VITE_FIREBASE_API_KEY: '',
  VITE_GOOGLE_CLIENT_ID: '',
  VITE_FIREBASE_AUTH_DOMAIN: '',
  VITE_FIREBASE_PROJECT_ID: '',
  VITE_SHARE_BASE_URL: '',
};

/**
 * The build's variables over the defaults, each named (see the module note).
 * A variable the build was not given keeps its default.
 *
 * Guarded because import.meta.env exists only under Vite: a test runner or a
 * script loading this file gets the defaults rather than a crash. The try is
 * the whole guard. A bare `import.meta.env` (an `if (import.meta.env)` test,
 * say) is itself replaced by the entire object, the leak this file avoids.
 *
 * @returns Every variable, defaults filled in.
 */
const getEnvVars = (): Record<keyof typeof defaultValues, unknown> => {
  try {
    const given = {
      VITE_API_URL: import.meta.env.VITE_API_URL,
      VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
      VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      VITE_SHARE_BASE_URL: import.meta.env.VITE_SHARE_BASE_URL,
    };
    const merged: Record<keyof typeof defaultValues, unknown> = { ...defaultValues };
    for (const name of Object.keys(given) as (keyof typeof given)[]) {
      if (given[name] !== undefined) merged[name] = given[name];
    }
    return merged;
  } catch (error) {
    console.warn('Error accessing environment variables:', error);
    return { ...defaultValues };
  }
};

/** The variables as read, once, at load. */
const rawEnvVars = getEnvVars();

/**
 * A variable as a string, whatever Vite handed over: booleans and numbers as
 * their text, a missing one as ''.
 *
 * @param value - The variable's raw value.
 * @returns It as a string.
 */
const ensureString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return '';
  return String(value);
};

/**
 * The backend's API address; production's when unset.
 * @deprecated 2026-10-04: nothing uses this (overnight review); to be deleted.
 */
export const VITE_API_URL = ensureString(rawEnvVars.VITE_API_URL);
/** Firebase's web API key, for signing in. */
export const VITE_FIREBASE_API_KEY = ensureString(rawEnvVars.VITE_FIREBASE_API_KEY);
/** The Google OAuth client id: Google Photos and One Tap. */
export const VITE_GOOGLE_CLIENT_ID = ensureString(rawEnvVars.VITE_GOOGLE_CLIENT_ID);
/** Firebase's own sign-in address, where the app does not serve the sign-in pages itself. */
export const VITE_FIREBASE_AUTH_DOMAIN = ensureString(rawEnvVars.VITE_FIREBASE_AUTH_DOMAIN);
/** The Firebase project. */
export const VITE_FIREBASE_PROJECT_ID = ensureString(rawEnvVars.VITE_FIREBASE_PROJECT_ID);
/**
 * Where share and garden links point (shared/api/shares shareBaseUrl): the
 * backend, or a subdomain pointed at it, so a pasted link shows a preview.
 * Empty means this app's own address.
 */
export const VITE_SHARE_BASE_URL = ensureString(rawEnvVars.VITE_SHARE_BASE_URL);

/** Every variable at once, raw (not forced to strings). */
export default rawEnvVars;
